import { useTranslation } from "react-i18next"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@f2e/ui/components/base"
import { useSearchParams } from "react-router"
import { BookOpen, Database } from "lucide-react"

import FoldersTab from "./components/FoldersTab"
import ConnectionsTab from "./components/ConnectionsTab"

const KnowledgeBasePage = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const rawTab = searchParams.get("tab")
  const currentTab =
    rawTab === "connections" || rawTab === "sources" ? "sources" : "folders"

  const handleTabChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (value === "folders") {
      newParams.delete("tab")
    } else {
      newParams.set("tab", "sources")
    }
    setSearchParams(newParams, { replace: false, flushSync: true })
  }

  const tabCards = [
    {
      key: "folders",
      icon: BookOpen,
      title: t("knowledgeBase.tabs.folders"),
    },
    {
      key: "sources",
      icon: Database,
      title: t("knowledgeBase.tabs.sources"),
    },
  ] as const

  return (
    <div className="w-full space-y-5">
      <div className="space-y-1.5 pl-2">
        <h1 className="text-2xl font-semibold leading-none tracking-tight text-slate-900 dark:text-slate-50">
          {t("knowledgeBase.page.title")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500 dark:text-slate-400">
          {t("knowledgeBase.page.description")}
        </p>
      </div>

      <Tabs
        value={currentTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList
          aria-label={t("knowledgeBase.page.title")}
          className="h-11 w-full justify-start gap-6 rounded-none border-b border-border bg-transparent p-0"
        >
          {tabCards.map((tab) => {
            const Icon = tab.icon
            return (
              <TabsTrigger
                key={tab.key}
                value={tab.key}
                className="h-11 flex-none rounded-none border-0 bg-transparent px-0 pb-3 pt-2 text-sm font-medium text-muted-foreground shadow-none transition-[box-shadow,color] hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-[inset_0_-2px_0_0_hsl(var(--foreground))]"
              >
                <Icon className="size-4 stroke-[2.25]" />
                <span>{tab.title}</span>
              </TabsTrigger>
            )
          })}
        </TabsList>

        <div className="mt-5">
          <div>
            <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
              {t(`knowledgeBase.sections.${currentTab}.title`)}
            </h4>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t(`knowledgeBase.sections.${currentTab}.summary`)}
            </p>
          </div>
        </div>

        <TabsContent value="folders" className="mt-5 w-full">
          <FoldersTab />
        </TabsContent>
        <TabsContent value="sources" className="mt-5 w-full">
          <ConnectionsTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default KnowledgeBasePage
